import { UserRound } from 'lucide-react';
import { isKeycloakClientRole } from '../../auth/clientRoles';
import { LiksButton } from '../../components/liks/LiksButton/LiksButton';
import { LiksDropdown } from '../../components/liks/LiksDropdown/LiksDropdown';
import { LiksFormFieldLabel } from '../../components/liks/LiksFormFieldLabel/LiksFormFieldLabel';
import { LiksPhotoUpload } from '../../components/liks/LiksPhotoUpload/LiksPhotoUpload';
import { LiksTextFormField } from '../../components/liks/LiksTextFormField/LiksTextFormField';
import type { CurrentUserProfile } from '../../types/user';
import { useTenantUserDetailsForm } from './useTenantUserDetailsForm';
import './TenantUserDetailsPage.css';

export type TenantUserDetailsFormProps = {
  profile: CurrentUserProfile;
  userId: string;
};

export function TenantUserDetailsForm({
  profile,
  userId,
}: TenantUserDetailsFormProps) {
  const {
    translate,
    photoFieldId,
    roleFieldId,
    photoDisplaySrc,
    photoError,
    handlePhotoChange,
    handleRemovePhoto,
    username,
    email,
    setEmail,
    firstName,
    setFirstName,
    lastName,
    setLastName,
    role,
    setRoleOverride,
    validFrom,
    setValidFrom,
    validTo,
    setValidTo,
    isPhotoBusy,
    isBusy,
    isSubmitting,
    isRoleLoading,
    isRoleLoadError,
    roleOptions,
    isEmailInvalid,
    isValidToBeforeValidFrom,
    isDirty,
    isSaveDisabled,
    handleCancel,
    handleSubmit,
  } = useTenantUserDetailsForm({ profile, userId });

  return (
    <form className="tenant-user-details__form" onSubmit={handleSubmit}>
      <div className="tenant-user-details__card">
        <div className="tenant-user-details__body">
          <div className="tenant-user-details__fields">
            <div className="tenant-user-details__field">
              <LiksFormFieldLabel htmlFor={roleFieldId} required>
                {translate('organisationManagement.users.roleLabel')}
              </LiksFormFieldLabel>
              {isRoleLoading ? (
                <p className="tenant-user-details__hint">
                  {translate(
                    'organisationManagement.users.details.roleLoading',
                  )}
                </p>
              ) : (
                <>
                  <LiksDropdown
                    id={roleFieldId}
                    className="tenant-user-details__dropdown"
                    variant="on-light"
                    value={role}
                    onChange={(value) => {
                      if (isKeycloakClientRole(value)) {
                        setRoleOverride(value);
                      }
                    }}
                    options={roleOptions}
                    aria-label={translate(
                      'organisationManagement.users.roleLabel',
                    )}
                    disabled={isSubmitting}
                  />
                  {isRoleLoadError && (
                    <p className="tenant-user-details__hint" role="alert">
                      {translate(
                        'organisationManagement.users.details.roleLoadError',
                      )}
                    </p>
                  )}
                </>
              )}
            </div>

            <div className="tenant-user-details__row">
              <LiksTextFormField
                label={translate(
                  'organisationManagement.users.details.emailLabel',
                )}
                required
                type="email"
                value={email}
                onChange={setEmail}
                disabled={isSubmitting}
                autoComplete="off"
                error={
                  isEmailInvalid
                    ? translate(
                        'organisationManagement.users.details.emailInvalid',
                      )
                    : null
                }
              />

              <LiksTextFormField
                label={translate(
                  'organisationManagement.users.details.usernameLabel',
                )}
                required
                value={username}
                disabled
                readOnly
                autoComplete="off"
              />
            </div>

            <div className="tenant-user-details__row">
              <LiksTextFormField
                label={translate(
                  'organisationManagement.users.details.firstNameLabel',
                )}
                required
                value={firstName}
                onChange={setFirstName}
                disabled={isSubmitting}
                autoComplete="off"
              />

              <LiksTextFormField
                label={translate(
                  'organisationManagement.users.details.lastNameLabel',
                )}
                required
                value={lastName}
                onChange={setLastName}
                disabled={isSubmitting}
                autoComplete="off"
              />
            </div>

            <div className="tenant-user-details__row">
              <LiksTextFormField
                label={translate(
                  'organisationManagement.users.details.validFromLabel',
                )}
                type="date"
                value={validFrom}
                onChange={setValidFrom}
                disabled={isSubmitting}
              />

              <LiksTextFormField
                label={translate(
                  'organisationManagement.users.details.validToLabel',
                )}
                type="date"
                value={validTo}
                onChange={setValidTo}
                disabled={isSubmitting}
                error={
                  isValidToBeforeValidFrom
                    ? translate(
                        'organisationManagement.users.details.validToBeforeValidFrom',
                      )
                    : null
                }
              />
            </div>
          </div>

          <LiksPhotoUpload
            className="tenant-user-details__photo"
            src={photoDisplaySrc}
            isLoading={isPhotoBusy}
            placeholder={<UserRound strokeWidth={1.5} aria-hidden />}
            inputId={photoFieldId}
            disabled={isBusy}
            onFileChange={(event) => {
              void handlePhotoChange(event);
            }}
            onRemove={handleRemovePhoto}
            error={photoError}
          />
        </div>

        <div className="tenant-user-details__actions">
          {isDirty ? (
            <LiksButton
              type="button"
              variant="secondary"
              disabled={isBusy}
              onClick={handleCancel}
            >
              {translate('organisationManagement.users.details.cancel')}
            </LiksButton>
          ) : null}
          <LiksButton type="submit" disabled={isSaveDisabled}>
            {translate('organisationManagement.users.details.save')}
          </LiksButton>
        </div>
      </div>
    </form>
  );
}
